import type { DiagramType } from '../../tools/interfaces/ITool';
import type {
  AgentCommandExecutor,
  AgentToolCall,
  AgentToolExecution,
  AgentToolPreview,
  AgentToolResult,
  IAgentContext,
  IAgentTool,
  IAgentToolContext,
  LLMToolDefinition,
} from './IAgentTool';

export class AgentToolRegistry {
  private tools: Map<string, IAgentTool> = new Map();
  // Runtime-only baseline: never serialize authorized scene facts into a preview payload.
  private previewScenes = new WeakMap<AgentToolPreview, string>();

  private previewScene(context: IAgentContext): string {
    return JSON.stringify(context.diagramData, (_key, value) => value && typeof value === 'object' && !Array.isArray(value)
      ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b))) : value);
  }

  private getPriorityIndexMap(assistantContext?: Record<string, unknown>): Map<string, number> {
    const priority = Array.isArray(assistantContext?.toolPriority)
      ? assistantContext.toolPriority
        .map((entry) => String(entry ?? '').trim())
        .filter((entry) => entry.length > 0)
      : [];
    return new Map(priority.map((toolName, index) => [toolName, index]));
  }

  private sortTools(tools: IAgentTool[], assistantContext?: Record<string, unknown>): IAgentTool[] {
    const priorityIndexMap = this.getPriorityIndexMap(assistantContext);
    const noPriorityRank = Number.MAX_SAFE_INTEGER;
    return [...tools].sort((a, b) => {
      const aRank = priorityIndexMap.get(a.name) ?? noPriorityRank;
      const bRank = priorityIndexMap.get(b.name) ?? noPriorityRank;
      if (aRank !== bRank) return aRank - bRank;
      return a.name.localeCompare(b.name);
    });
  }

  register(tool: IAgentTool): void {
    this.tools.set(tool.name, tool);
  }

  unregister(name: string): void {
    this.tools.delete(name);
  }

  getTool(name: string): IAgentTool | undefined {
    return this.tools.get(name);
  }

  getAvailableTools(diagramType: DiagramType, assistantContext?: Record<string, unknown>): IAgentTool[] {
    const tools = Array.from(this.tools.values()).filter((tool) => {
      if (!tool.supportedDiagramTypes || tool.supportedDiagramTypes.length === 0) return true;
      return tool.supportedDiagramTypes.includes(diagramType);
    });
    return this.sortTools(tools, assistantContext);
  }

  buildToolDefinitions(diagramType: DiagramType, assistantContext?: Record<string, unknown>): LLMToolDefinition[] {
    return this.getAvailableTools(diagramType, assistantContext).map((tool) => ({
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters,
      tier: tool.tier,
      category: tool.category,
    }));
  }

  private toToolContext(context: IAgentContext): IAgentToolContext {
    const toolContext: IAgentToolContext = {
      diagramData: context.diagramData,
      diagramType: context.diagramType,
      selectedNodeIds: context.selectedNodeIds,
      canWrite: context.canWrite,
      stores: context.stores,
    };
    if (context.structuralDiagramData) toolContext.structuralDiagramData = context.structuralDiagramData;
    if (context.uiLocale) toolContext.uiLocale = context.uiLocale;
    if (context.userRequest !== undefined) toolContext.userRequest = context.userRequest;
    if (context.assistantContext) toolContext.assistantContext = context.assistantContext;
    return toolContext;
  }

  private createCommandExecutor(context: IAgentContext): AgentCommandExecutor {
    return {
      execute: (command) => context.commandManager.execute(command),
      executeBatch: (commands, batchName) => context.commandManager.executeBatch(commands, batchName),
    };
  }

  private async executeAction(
    action: AgentToolExecution | undefined,
    context: IAgentContext,
  ): Promise<Partial<AgentToolResult> | undefined> {
    if (!action) return undefined;

    if (this.actionRequiresWrite(action) && !context.canWrite) {
      return {
        success: false,
        summary: 'You do not have write access to this diagram.',
      };
    }

    if (action.kind === 'command') {
      await context.commandManager.execute(action.command);
      return undefined;
    }

    if (action.kind === 'commands') {
      await context.commandManager.executeBatch(action.commands, action.batchName);
      return undefined;
    }

    if (action.kind === 'command-sequence') {
      return (await action.run(this.createCommandExecutor(context))) ?? undefined;
    }

    return (await action.run()) ?? undefined;
  }

  private actionRequiresWrite(action: AgentToolExecution): boolean {
    return (
      action.kind === 'command'
      || action.kind === 'commands'
      || action.kind === 'command-sequence'
      || action.kind === 'external-effect'
    );
  }

  private stripImmediateExecution(result: AgentToolResult): AgentToolResult {
    if (!result.execution) return result;
    const publicResult = { ...result };
    delete publicResult.execution;
    return publicResult;
  }

  async resolvePreview(
    preview: AgentToolPreview,
    action: 'confirm' | 'reject',
    context: IAgentContext,
  ): Promise<Partial<AgentToolResult> | undefined> {
    const baseline = this.previewScenes.get(preview);
    if (action === 'confirm' && baseline !== undefined && baseline !== this.previewScene(context)) {
      return { success: false, summary: 'The diagram changed after this preview. Prepare a new proposal before applying.' };
    }
    return this.executeAction(action === 'confirm' ? preview.confirm : preview.reject, context);
  }

  async executeToolCall(
    name: string,
    params: Record<string, unknown>,
    context: IAgentContext,
  ): Promise<AgentToolResult> {
    const tool = this.tools.get(name);
    if (!tool) {
      // The model invented a tool name (a common failure is reaching for a
      // remove_*/delete_* tool that doesn't exist in this domain). Steer it
      // back to a real tool instead of dead-ending, so the loop can retry.
      const available = Array.from(this.tools.keys()).join(', ');
      return {
        success: false,
        summary:
          `Unknown tool "${name}" — it does not exist. Do not invent tool names. ` +
          `Pick one of the available tools and call it instead: ${available}.`,
      };
    }

    if (tool.tier !== 'safe' && !context.canWrite) {
      return { success: false, summary: 'You do not have write access to this diagram.' };
    }

    // An Atlas saved custom view is functionally read-only: scenario
    // modeling never commits for it (the backend rejects a non-canonical
    // diagram). Each scenario tool also checks this itself, but a shared
    // gate here means a future scenario-category tool can't forget to.
    if (tool.category === 'scenario' && context.assistantContext?.isSavedAtlasView) {
      return { success: false, summary: 'Scenario modeling is not available on a saved custom view.' };
    }

    const result = await tool.execute(params, this.toToolContext(context));
    if (result.success && result.preview && tool.tier === 'structural' && ['network', 'genogram'].includes(context.diagramType)) {
      this.previewScenes.set(result.preview, this.previewScene(context));
    }
    if (result.success && result.execution) {
      const actionResult = await this.executeAction(result.execution, context);
      return {
        ...this.stripImmediateExecution(result),
        ...(actionResult ?? {}),
      };
    }

    return result;
  }

  async executeToolCalls(
    calls: AgentToolCall[],
    context: IAgentContext,
  ): Promise<Array<{ call: AgentToolCall; result: AgentToolResult }>> {
    const results: Array<{ call: AgentToolCall; result: AgentToolResult }> = [];
    for (const call of calls) {
      const result = await this.executeToolCall(call.name, call.params, context);
      results.push({ call, result });
    }
    return results;
  }
}
